import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  BookOpen, 
  Inbox, 
  LogOut, 
  Clock, 
  Mail, 
  MailOpen, 
  Trash2, 
  RefreshCw,
  User,
  X,
  Calendar,
  Video,
  Sparkles,
  Star,
  Archive,
  Search,
  Filter,
  CheckCheck,
  MailPlus,
  AlertCircle,
  Bell,
  Tag,
  ChevronDown,
  MoreHorizontal,
  ArrowRight,
  Menu,
  FileDown,
  Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const t = new URLSearchParams(window.location.search).get("tab");
      if (t === "booklet" || t === "inbox" || t === "course") return t;
    }
    return "course";
  });
  const [courseInfo, setCourseInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, starred: 0, archived: 0, categories: {} });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [inboxView, setInboxView] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [isSearching, setIsSearching] = useState(false);
  
  const lastMessageCountRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(permission => {
        setNotificationsEnabled(permission === "granted");
      });
    } else if ("Notification" in window && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
  }, []);

  useEffect(() => {
    fetchCourseInfo();
    fetchMessages();
    fetchStats();
    pollingIntervalRef.current = setInterval(() => {
      checkForNewMessages();
    }, 5000);
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const url = new URL(window.location.href);
    const payment = url.searchParams.get("payment");
    const sessionId = url.searchParams.get("session_id");
    if (payment !== "success" || !sessionId) return;

    // Clean query string
    window.history.replaceState({}, "", "/dashboard");

    const toastId = toast.loading("جارٍ التحقق من عملية الدفع...");
    let attempts = 0;
    const maxAttempts = 6;
    const pollInterval = 2500;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        const { payment_status, status } = res.data;
        if (payment_status === "paid") {
          toast.success("🎉 تم تأكيد دفعتك وتسجيلك في الدورة!", { id: toastId, duration: 6000 });
          await checkAuth();
          fetchCourseInfo();
          fetchMessages();
          fetchStats();
          return;
        }
        if (status === "expired" || payment_status === "failed") {
          toast.error("انتهت جلسة الدفع أو فشلت. حاول مجدداً.", { id: toastId });
          return;
        }
        if (attempts >= maxAttempts) {
          toast.message("جارٍ معالجة الدفع — سيصلك التأكيد خلال دقائق.", { id: toastId });
          return;
        }
        setTimeout(poll, pollInterval);
      } catch (e) {
        console.error("Payment polling failed:", e);
        toast.error("تعذّر التحقق من الدفع. تواصل معنا إذا استمرت المشكلة.", { id: toastId });
      }
    };
    poll();
  }, [checkAuth]);

  const checkForNewMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/stats`, { withCredentials: true });
      const newStats = response.data;
      if (newStats.unread > stats.unread && lastMessageCountRef.current > 0) {
        const newCount = newStats.unread - stats.unread;
        toast.info(`لديك ${newCount} رسالة جديدة!`, {
          action: {
            label: "عرض",
            onClick: () => { setActiveTab("inbox"); setInboxView("unread"); }
          }
        });
        if (notificationsEnabled && document.hidden) {
          new Notification("رسالة جديدة", {
            body: `لديك ${newCount} رسالة جديدة في بريدك`,
            icon: "/favicon.ico"
          });
        }
        if (activeTab === "inbox") fetchMessages();
      }
      lastMessageCountRef.current = newStats.total;
      setStats(newStats);
    } catch (error) {
      console.error("Failed to check for new messages:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "inbox") fetchMessages();
  }, [activeTab, inboxView, filterCategory]);

  const fetchCourseInfo = async () => {
    try {
      const response = await axios.get(`${API}/course-info`);
      setCourseInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch course info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = useCallback(async () => {
    try {
      const archived = inboxView === "archived";
      const response = await axios.get(`${API}/messages?archived=${archived}`, { withCredentials: true });
      let filteredMessages = response.data;
      if (inboxView === "unread") filteredMessages = filteredMessages.filter(m => !m.read);
      else if (inboxView === "starred") filteredMessages = filteredMessages.filter(m => m.starred);
      if (filterCategory !== "all") filteredMessages = filteredMessages.filter(m => m.category === filterCategory);
      setMessages(filteredMessages);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, [inboxView, filterCategory]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/messages/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchMessages(); return; }
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/messages/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) { toast.error("فشل البحث"); }
    finally { setIsSearching(false); }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      setMessages(messages.map(m => m.message_id === messageId ? { ...m, read: true } : m));
      fetchStats();
    } catch (error) { toast.error("فشل تحديث الرسالة"); }
  };

  const handleMarkAsUnread = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/unread`, {}, { withCredentials: true });
      setMessages(messages.map(m => m.message_id === messageId ? { ...m, read: false } : m));
      fetchStats();
    } catch (error) { toast.error("فشل تحديث الرسالة"); }
  };

  const handleToggleStar = async (messageId, e) => {
    e?.stopPropagation();
    try {
      const response = await axios.put(`${API}/messages/${messageId}/star`, {}, { withCredentials: true });
      setMessages(messages.map(m => m.message_id === messageId ? { ...m, starred: response.data.starred } : m));
      fetchStats();
    } catch (error) { toast.error("فشل التحديث"); }
  };

  const handleArchive = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/archive`, {}, { withCredentials: true });
      setMessages(messages.filter(m => m.message_id !== messageId));
      if (selectedMessage?.message_id === messageId) setSelectedMessage(null);
      toast.success("تم أرشفة الرسالة");
      fetchStats();
    } catch (error) { toast.error("فشل الأرشفة"); }
  };

  const handleUnarchive = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/unarchive`, {}, { withCredentials: true });
      setMessages(messages.filter(m => m.message_id !== messageId));
      if (selectedMessage?.message_id === messageId) setSelectedMessage(null);
      toast.success("تم استعادة الرسالة");
      fetchStats();
    } catch (error) { toast.error("فشل الاستعادة"); }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/messages/mark-all-read`, {}, { withCredentials: true });
      setMessages(messages.map(m => ({ ...m, read: true })));
      toast.success("تم تحديد الكل كمقروء");
      fetchStats();
    } catch (error) { toast.error("فشلت العملية"); }
  };

  const handleBulkAction = async (action) => {
    if (selectedMessages.length === 0) { toast.error("لم يتم تحديد أي رسائل"); return; }
    try {
      await axios.post(`${API}/messages/bulk-action?action=${action}`, selectedMessages, { withCredentials: true });
      if (action === "delete" || action === "archive") {
        setMessages(messages.filter(m => !selectedMessages.includes(m.message_id)));
      } else if (action === "read") {
        setMessages(messages.map(m => selectedMessages.includes(m.message_id) ? { ...m, read: true } : m));
      } else if (action === "unread") {
        setMessages(messages.map(m => selectedMessages.includes(m.message_id) ? { ...m, read: false } : m));
      } else if (action === "star") {
        setMessages(messages.map(m => selectedMessages.includes(m.message_id) ? { ...m, starred: true } : m));
      }
      setSelectedMessages([]);
      toast.success("تم تنفيذ الإجراء");
      fetchStats();
    } catch (error) { toast.error("فشل الإجراء"); }
  };

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      await axios.delete(`${API}/messages/${messageToDelete}`, { withCredentials: true });
      setMessages(messages.filter(m => m.message_id !== messageToDelete));
      if (selectedMessage?.message_id === messageToDelete) setSelectedMessage(null);
      toast.success("تم حذف الرسالة");
      fetchStats();
    } catch (error) { toast.error("فشل الحذف"); }
    finally { setDeleteDialogOpen(false); setMessageToDelete(null); }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) handleMarkAsRead(message.message_id);
  };

  const toggleMessageSelection = (messageId, e) => {
    e?.stopPropagation();
    setSelectedMessages(prev => prev.includes(messageId) ? prev.filter(id => id !== messageId) : [...prev, messageId]);
  };

  const selectAllMessages = () => {
    if (selectedMessages.length === messages.length) setSelectedMessages([]);
    else setSelectedMessages(messages.map(m => m.message_id));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
    if (tab === "inbox") setInboxView("all");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return date.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' });
    else if (days === 1) return 'أمس';
    else if (days < 7) return date.toLocaleDateString('ar', { weekday: 'short' });
    else return date.toLocaleDateString('ar', { month: 'short', day: 'numeric' });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      general: "bg-gray-100 text-gray-800",
      announcement: "bg-blue-100 text-blue-800",
      update: "bg-green-100 text-green-800",
      important: "bg-red-100 text-red-800"
    };
    return colors[category] || colors.general;
  };

  const getCategoryLabel = (category) => {
    const labels = { general: "عام", announcement: "إعلان", update: "تحديث", important: "مهم" };
    return labels[category] || "عام";
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'announcement': return <Bell className="w-3 h-3" />;
      case 'update': return <RefreshCw className="w-3 h-3" />;
      case 'important': return <AlertCircle className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open': return <span className="badge-success">التسجيل مفتوح</span>;
      case 'closed': return <span className="badge-error">التسجيل مغلق</span>;
      default: return <span className="badge-info">قريبًا</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFF]" data-testid="user-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-[#F0F4FF] rounded-lg"
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <span className="font-bold text-[#1A1D3D] text-sm" style={{ fontFamily: 'Cairo, sans-serif' }}>لوحة التحكم</span>
        </div>
        <div className="flex items-center gap-2">
          {stats.unread > 0 && activeTab !== "inbox" && (
            <button onClick={() => handleTabChange("inbox")} className="relative p-2">
              <Inbox className="w-5 h-5 text-[#4A5078]" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] rounded-full text-white text-[10px] font-bold flex items-center justify-center">{stats.unread}</span>
            </button>
          )}
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar - RTL: right side */}
      <aside className={`fixed right-0 top-0 h-full w-64 bg-white border-l border-[#E2E8F0] z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'} lg:translate-x-0`}>
        <div className="p-6">
          <div className="hidden lg:flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#1A1D3D]" style={{ fontFamily: 'Cairo, sans-serif' }}>
              وعي الغذاء صلاة
            </span>
          </div>
          <div className="lg:hidden h-10"></div>

          <nav className="space-y-2">
            <button
              onClick={() => navigate('/')}
              className="sidebar-link w-full"
              data-testid="nav-home"
            >
              <ArrowRight className="w-5 h-5" />
              <span>العودة للرئيسية</span>
            </button>
            <button
              onClick={() => handleTabChange("course")}
              className={`sidebar-link w-full ${activeTab === "course" ? "active" : ""}`}
              data-testid="nav-course"
            >
              <BookOpen className="w-5 h-5" />
              <span>الدورة</span>
            </button>
            <button
              onClick={() => handleTabChange("inbox")}
              className={`sidebar-link w-full ${activeTab === "inbox" ? "active" : ""}`}
              data-testid="nav-inbox"
            >
              <div className="relative">
                <Video className="w-5 h-5" />
                {stats.unread > 0 && activeTab !== "inbox" && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#EC4899] rounded-full animate-pulse"></span>
                )}
              </div>
              <span>روابط Zoom</span>
              {stats.unread > 0 && (
                <span className="unread-badge mr-auto">{stats.unread}</span>
              )}
            </button>
            <button
              onClick={() => handleTabChange("booklet")}
              className={`sidebar-link w-full ${activeTab === "booklet" ? "active" : ""}`}
              data-testid="nav-booklet"
            >
              <FileDown className="w-5 h-5" />
              <span>تحميل الكتيب المجاني</span>
            </button>
          </nav>
        </div>

        {/* User Profile */}
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-[#E2E8F0]">
          <div className="flex items-center gap-3 mb-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-200" />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1A1D3D] truncate">{user?.name}</p>
              <p className="text-xs text-[#8B92B3] truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-[#E2E8F0] hover:bg-gradient-to-r hover:from-[#F0F4FF] hover:to-[#FCE7F3] hover:border-[#7C3AED] text-[#4A5078]"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 ml-2" />
            تسجيل الخروج
          </Button>
        </div>
      </aside>

      {/* Main Content - RTL: margin-right for sidebar */}
      <main className="lg:mr-64 p-4 lg:p-6 pt-20 lg:pt-6">
        {/* Course Tab */}
        {activeTab === "course" && (
          <div data-testid="course-section">
            <div className="mb-6 lg:mb-8">
              <h1 className="heading-2 mb-2 text-xl lg:text-3xl">مرحبًا، {user?.name?.split(' ')[0]}!</h1>
              <p className="body-text text-sm lg:text-base">شكرًا لاهتمامك بدورة وعي الغذاء صلاة.</p>
            </div>

            {/* Mobile-only quick access to Zoom Links */}
            <button
              onClick={() => handleTabChange("inbox")}
              className="lg:hidden w-full mb-6 group relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#7C3AED] via-[#EC4899] to-[#F97316] p-5 text-white shadow-xl shadow-purple-500/30 active:scale-[0.98] transition-transform"
              data-testid="mobile-zoom-shortcut"
            >
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMyIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')] opacity-50"></div>
              <div className="relative flex items-center gap-4">
                <div className="relative w-14 h-14 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 ring-2 ring-white/30">
                  <Video className="w-7 h-7 text-white" />
                  {stats.unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 bg-white text-[#EC4899] rounded-full text-xs font-bold flex items-center justify-center shadow-lg">
                      {stats.unread}
                    </span>
                  )}
                </div>
                <div className="flex-1 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider opacity-90 mb-0.5">روابط الجلسات</p>
                  <p className="text-lg font-bold leading-tight">روابط Zoom</p>
                  <p className="text-xs opacity-90 mt-0.5">
                    {stats.unread > 0 ? `لديك ${stats.unread} رابط جديد` : "اضغط لعرض الروابط"}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 rotate-180 flex-shrink-0 group-active:-translate-x-1 transition-transform" />
              </div>
            </button>

            {loading ? (
              <div className="card-dashboard p-6 animate-pulse">
                <div className="bg-[#E4E4E7] h-8 rounded w-1/2 mb-4"></div>
                <div className="bg-[#E4E4E7] h-4 rounded w-3/4 mb-2"></div>
                <div className="bg-[#E4E4E7] h-4 rounded w-2/3"></div>
              </div>
            ) : (
              <div className="card-dashboard overflow-hidden">
                <div className="bg-gradient-to-r from-[#7C3AED] via-[#EC4899] to-[#F97316] p-5 lg:p-8 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMyIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIi8+PC9zdmc+')] opacity-50"></div>
                  <div className="relative flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 lg:w-8 lg:h-8" />
                    <h2 className="text-lg lg:text-2xl font-bold" style={{ fontFamily: 'Cairo, sans-serif' }}>
                      وعي الغذاء صلاة
                    </h2>
                  </div>
                  {getStatusBadge(courseInfo?.status)}
                </div>

                <div className="p-5 lg:p-8">
                  <p className="body-large mb-6 lg:mb-8 text-sm lg:text-lg">
                    {courseInfo?.description || "دورة تحويلية لفتح طبقات الأكل العاطفي وفهم الرابط الخفي بين المشاعر والجسد."}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
                    {courseInfo?.start_date && (
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#F0F4FF] to-[#FCE7F3] rounded-xl">
                        <Calendar className="w-6 h-6 text-[#7C3AED]" />
                        <div>
                          <p className="text-xs text-[#8B92B3]">تاريخ البداية</p>
                          <p className="font-medium text-[#1A1D3D]">{courseInfo.start_date}</p>
                        </div>
                      </div>
                    )}
                    {courseInfo?.duration && (
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#FCE7F3] to-[#FEF3C7] rounded-xl">
                        <Clock className="w-6 h-6 text-[#EC4899]" />
                        <div>
                          <p className="text-xs text-[#8B92B3]">المدة</p>
                          <p className="font-medium text-[#1A1D3D]">{courseInfo.duration}</p>
                        </div>
                      </div>
                    )}
                    {courseInfo?.sessions && (
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#FEF3C7] to-[#ECFDF5] rounded-xl">
                        <Video className="w-6 h-6 text-[#F97316]" />
                        <div>
                          <p className="text-xs text-[#8B92B3]">الجلسات المباشرة</p>
                          <p className="font-medium text-[#1A1D3D]">{courseInfo.sessions} جلسات</p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`rounded-lg p-4 lg:p-6 ${user?.enrolled ? 'bg-gradient-to-r from-[#ECFDF5] to-[#DCFCE7] border border-[#10B981]/30' : 'bg-[#F4F4F5]'}`} data-testid="enrollment-panel">
                    <h4 className="heading-4 mb-2 text-sm lg:text-lg flex items-center gap-2">
                      {user?.enrolled ? (
                        <>
                          <CheckCheck className="w-5 h-5 text-[#10B981]" />
                          <span className="text-[#059669]">أنت مُسجّل في الدورة!</span>
                        </>
                      ) : (
                        "تم تسجيلك!"
                      )}
                    </h4>
                    <p className="text-[#52525B] text-sm lg:text-base">
                      {user?.enrolled
                        ? "تم تأكيد دفعتك ومكانك في الجلسات الأربع. روابط Zoom ستصلك عبر صندوق الوارد قبل كل جلسة."
                        : "ستتلقى التحديثات حول الدورة في بريدك الوارد."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Inbox Tab */}
        {activeTab === "inbox" && (
          <div data-testid="inbox-section">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 lg:mb-6">
              <div>
                <h1 className="heading-2 mb-1 text-xl lg:text-3xl">روابط Zoom</h1>
                <div className="flex items-center gap-3 lg:gap-4 text-xs lg:text-sm text-[#71717A]">
                  <span>{stats.total} إجمالي</span>
                  <span>{stats.unread} غير مقروء</span>
                  <span>{stats.starred} مميز</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="border-[#E4E4E7] text-xs lg:text-sm" disabled={stats.unread === 0}>
                  <CheckCheck className="w-4 h-4 ml-1 lg:ml-2" />
                  <span className="hidden sm:inline">تحديد الكل كمقروء</span>
                  <span className="sm:hidden">قراءة الكل</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => { fetchMessages(); fetchStats(); }} className="border-[#E4E4E7]" data-testid="refresh-inbox">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="card-dashboard p-3 lg:p-4 mb-4">
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                  <Input
                    placeholder="بحث في الروابط..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pr-10 text-sm"
                    data-testid="search-input"
                  />
                </div>
                
                <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  <Button variant={inboxView === "all" ? "default" : "outline"} size="sm" onClick={() => setInboxView("all")} className={`flex-shrink-0 text-xs lg:text-sm ${inboxView === "all" ? "bg-[#7C3AED]" : "border-[#E4E4E7]"}`}>
                    <Inbox className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1" />الكل
                  </Button>
                  <Button variant={inboxView === "unread" ? "default" : "outline"} size="sm" onClick={() => setInboxView("unread")} className={`flex-shrink-0 text-xs lg:text-sm ${inboxView === "unread" ? "bg-[#7C3AED]" : "border-[#E4E4E7]"}`}>
                    <Mail className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1" />غير مقروء
                    {stats.unread > 0 && <span className="mr-1">({stats.unread})</span>}
                  </Button>
                  <Button variant={inboxView === "starred" ? "default" : "outline"} size="sm" onClick={() => setInboxView("starred")} className={`flex-shrink-0 text-xs lg:text-sm ${inboxView === "starred" ? "bg-[#7C3AED]" : "border-[#E4E4E7]"}`}>
                    <Star className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1" />المميز
                  </Button>
                  <Button variant={inboxView === "archived" ? "default" : "outline"} size="sm" onClick={() => setInboxView("archived")} className={`flex-shrink-0 text-xs lg:text-sm ${inboxView === "archived" ? "bg-[#7C3AED]" : "border-[#E4E4E7]"}`}>
                    <Archive className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1" />الأرشيف
                  </Button>
                </div>
              </div>

              {selectedMessages.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#E4E4E7]">
                  <span className="text-xs lg:text-sm text-[#71717A]">{selectedMessages.length} محدد</span>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleBulkAction("read")}>مقروء</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleBulkAction("unread")}>غير مقروء</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleBulkAction("star")}><Star className="w-3.5 h-3.5 ml-1" />تمييز</Button>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => handleBulkAction("archive")}><Archive className="w-3.5 h-3.5 ml-1" />أرشفة</Button>
                  <Button variant="outline" size="sm" className="text-xs text-red-500" onClick={() => handleBulkAction("delete")}><Trash2 className="w-3.5 h-3.5 ml-1" />حذف</Button>
                </div>
              )}
            </div>

            <div className="card-dashboard">
              {messages.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-3">
                  <div className={`lg:col-span-1 border-l border-[#E4E4E7] ${selectedMessage ? 'hidden lg:block' : 'block'}`}>
                    <div className="p-3 border-b border-[#E4E4E7] flex items-center gap-3">
                      <Checkbox checked={selectedMessages.length === messages.length && messages.length > 0} onCheckedChange={selectAllMessages} />
                      <span className="text-xs lg:text-sm text-[#71717A]">تحديد الكل</span>
                    </div>
                    <ScrollArea className="h-[450px] lg:h-[550px]">
                      {messages.map((message) => (
                        <div
                          key={message.message_id}
                          onClick={() => openMessage(message)}
                          className={`p-3 lg:p-4 cursor-pointer transition-all border-b border-[#E4E4E7] hover:bg-[#F4F4F5] ${message.read ? '' : 'bg-[#F8FAFC] border-r-4 border-r-[#7C3AED]'} ${selectedMessage?.message_id === message.message_id ? 'bg-[#EEF2FF]' : ''}`}
                          data-testid={`message-item-${message.message_id}`}
                        >
                          <div className="flex items-start gap-2 lg:gap-3">
                            <Checkbox checked={selectedMessages.includes(message.message_id)} onCheckedChange={(e) => toggleMessageSelection(message.message_id, e)} onClick={(e) => e.stopPropagation()} className="mt-1" />
                            <button onClick={(e) => handleToggleStar(message.message_id, e)} className={`mt-1 flex-shrink-0 ${message.starred ? 'text-yellow-500' : 'text-[#D1D5DB] hover:text-yellow-500'}`}>
                              <Star className={`w-4 h-4 ${message.starred ? 'fill-current' : ''}`} />
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs lg:text-sm truncate ${message.read ? 'text-[#52525B]' : 'text-[#0A0A0A] font-semibold'}`}>{message.sender_name}</span>
                                <span className="text-[10px] lg:text-xs text-[#A1A1AA] flex-shrink-0 mr-2">{formatDate(message.created_at)}</span>
                              </div>
                              <p className={`text-xs lg:text-sm truncate ${message.read ? 'text-[#71717A]' : 'text-[#0A0A0A] font-medium'}`}>{message.subject}</p>
                              <div className="flex items-center gap-2 mt-1.5 lg:mt-2">
                                <span className="inline-flex items-center gap-1 px-1.5 lg:px-2 py-0.5 rounded-full text-[10px] lg:text-xs bg-[#F0F4FF] text-[#7C3AED] border border-[#7C3AED]/20">
                                  <Video className="w-3 h-3" />Zoom
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>

                  <div className={`lg:col-span-2 ${selectedMessage ? 'block' : 'hidden lg:block'}`}>
                    {selectedMessage ? (
                      <div className="h-full flex flex-col">
                        <div className="p-4 lg:p-6 border-b border-[#E4E4E7]">
                          <button onClick={() => setSelectedMessage(null)} className="lg:hidden flex items-center gap-1 text-sm text-[#7C3AED] mb-3">
                            <ArrowRight className="w-4 h-4" />
                            العودة للبريد
                          </button>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#F0F4FF] text-[#7C3AED] border border-[#7C3AED]/20">
                                  <Video className="w-3 h-3" />Zoom
                                </span>
                                {!selectedMessage.read && <Badge variant="secondary" className="bg-[#7C3AED] text-white">جديد</Badge>}
                              </div>
                              <h3 className="heading-3 mb-2 text-base lg:text-xl">{selectedMessage.subject}</h3>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs lg:text-sm text-[#71717A]">
                                <span>من: <strong className="text-[#0A0A0A]">{selectedMessage.sender_name}</strong></span>
                                <span>{new Date(selectedMessage.created_at).toLocaleString('ar')}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 mr-2">
                              <Button variant="ghost" size="sm" onClick={(e) => handleToggleStar(selectedMessage.message_id, e)} className={selectedMessage.starred ? 'text-yellow-500' : 'text-[#71717A]'}>
                                <Star className={`w-4 h-4 ${selectedMessage.starred ? 'fill-current' : ''}`} />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {selectedMessage.read ? (
                                    <DropdownMenuItem onClick={() => handleMarkAsUnread(selectedMessage.message_id)}><Mail className="w-4 h-4 ml-2" />تحديد كغير مقروء</DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleMarkAsRead(selectedMessage.message_id)}><MailOpen className="w-4 h-4 ml-2" />تحديد كمقروء</DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  {inboxView === "archived" ? (
                                    <DropdownMenuItem onClick={() => handleUnarchive(selectedMessage.message_id)}><ArrowRight className="w-4 h-4 ml-2" />نقل للبريد</DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleArchive(selectedMessage.message_id)}><Archive className="w-4 h-4 ml-2" />أرشفة</DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => { setMessageToDelete(selectedMessage.message_id); setDeleteDialogOpen(true); }} className="text-red-500"><Trash2 className="w-4 h-4 ml-2" />حذف</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                        <ScrollArea className="flex-1 p-4 lg:p-6">
                          <div className="prose prose-sm max-w-none">
                            <p className="text-[#52525B] whitespace-pre-wrap leading-relaxed text-sm lg:text-base">{selectedMessage.content}</p>
                          </div>
                        </ScrollArea>
                      </div>
                    ) : (
                      <div className="h-[600px] flex items-center justify-center text-center">
                        <div>
                          <MailOpen className="w-16 h-16 text-[#D1D5DB] mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-[#71717A] mb-2">لم يتم تحديد رسالة</h3>
                          <p className="text-sm text-[#A1A1AA]">اختر رسالة من القائمة لقراءتها</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-16 text-center">
                  <div className="w-20 h-20 bg-[#F4F4F5] rounded-full flex items-center justify-center mx-auto mb-6">
                    {inboxView === "archived" ? <Archive className="w-10 h-10 text-[#A1A1AA]" /> : inboxView === "starred" ? <Star className="w-10 h-10 text-[#A1A1AA]" /> : <Inbox className="w-10 h-10 text-[#A1A1AA]" />}
                  </div>
                  <h3 className="heading-4 mb-2">
                    {inboxView === "archived" ? "لا توجد رسائل مؤرشفة" : inboxView === "starred" ? "لا توجد روابط مميزة" : inboxView === "unread" ? "تم قراءة الكل!" : "لم تصلك روابط Zoom بعد"}
                  </h3>
                  <p className="text-small max-w-sm mx-auto">
                    {inboxView === "archived" ? "الرسائل المؤرشفة ستظهر هنا." : inboxView === "starred" ? "ميّز الروابط المهمة للعثور عليها بسرعة." : inboxView === "unread" ? "لقد قرأت جميع رسائلك." : "ستصلك روابط Zoom للجلسات هنا قبل كل جلسة."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Booklet Tab */}
        {activeTab === "booklet" && (
          <div data-testid="booklet-section" className="max-w-3xl mx-auto">
            <div className="mb-6 lg:mb-8">
              <p className="overline mb-2">هدية مجانية</p>
              <h1 className="heading-1 text-2xl lg:text-4xl mb-2">تحميل الكتيب المجاني</h1>
              <p className="text-small">ارفِق الكتيب رفيقك في رحلتك مع وعي الغذاء.</p>
            </div>

            <div className="bg-gradient-to-br from-[#F0F4FF] via-[#FCE7F3] to-[#FEF3C7] rounded-3xl p-8 lg:p-12 border border-[#E2E8F0] shadow-lg">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/25">
                  <FileDown className="w-10 h-10 text-white" />
                </div>
                <h2 className="heading-3 text-xl lg:text-2xl mb-3">كتيب وعي الغذاء صلاة</h2>
                <p className="text-base text-[#4A5078] mb-8 max-w-md">
                  دليل عملي وتطبيقي يُرافقك خلال رحلتك في الدورة، يساعدك على تعميق الفهم وتطبيق الوعي على حياتك اليومية.
                </p>
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:from-[#6D28D9] hover:to-[#DB2777] text-white py-6 px-8 text-base lg:text-lg rounded-xl shadow-lg shadow-purple-500/25 transition-all hover:scale-[1.03]"
                  data-testid="booklet-download-button"
                >
                  <a
                    href="/assets/booklet.pdf"
                    download="كتيب-وعي-الغذاء-صلاة.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="w-5 h-5 ml-2" />
                    تحميل الكتيب الآن
                  </a>
                </Button>
                <p className="text-xs text-[#8B92B3] mt-4">ملف PDF — رفيقك التطبيقي خلال رحلة الكورس</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف الرسالة</AlertDialogTitle>
            <AlertDialogDescription>هل أنت متأكد من حذف هذه الرسالة؟ لا يمكن التراجع عن هذا الإجراء.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={handleDeleteMessage} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">حذف</AlertDialogAction>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
